import type {
    MutableToolStep,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import type { EntityItem } from '@toolproof-core/visualization/_contract/item';
import type {
    EntityItemCollectionDerivation,
    EntityItemDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    resolveCasesConfig,
    resolveToolStepLayoutConfig,
} from '@/workflow/_pure/config/derivationConfig';
import { deriveCaseBandGeometry } from '@/workflow/_pure/derivation/caseGeometry';
import { buildCaseItemCollectionDerivations } from '@/workflow/_pure/derivation/collections';
import { toCaseItemDerivation } from '@/workflow/_pure/derivation/itemFactories';
import { deriveCaseMembership } from '@/workflow-builder/_pure/derivation/caseMembership';
import { deriveToolStepRingLayout } from '@/workflow-builder/_pure/derivation/committedProjection';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { CaseMetadata } from '@/workflow/_contract/kernel';
import type { WorkflowKeyScope } from '@/workflow/_pure/keyCodec';
import type { CaseSeed } from '@/workflow-builder/_contract/derivation';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';

export function deriveCaseItemCollectionDerivations(args: {
    cosmosData: CosmosDataModel;
    runtimeState: RuntimeState;
    projectedToolSteps: readonly MutableToolStep[];
    config: Config;
    workflowKeyScope: WorkflowKeyScope;
}): {
    itemCollectionDerivations: EntityItemCollectionDerivation[];
    metadataByItemKey: Map<EntityItem['itemKey'], CaseMetadata>;
} {
    const caseConfig = resolveCasesConfig(args.config);
    if (!caseConfig.enabled) {
        return {
            itemCollectionDerivations: [],
            metadataByItemKey: new Map(),
        };
    }

    const toolStepLayoutConfig = resolveToolStepLayoutConfig(args.config);
    const { startRadius, ringGap, ringRadii } = deriveToolStepRingLayout({
        cosmosData: args.cosmosData,
        projectedToolSteps: args.projectedToolSteps,
        config: args.config,
    });
    const tubeRadii = args.projectedToolSteps.map(() => toolStepLayoutConfig.tubeRadius);

    const memberships = deriveCaseMembership(args);

    const toRingRadius = (stepIndex: number) => ringRadii[stepIndex] ?? (startRadius + stepIndex * ringGap);
    const toTubeRadius = (stepIndex: number) => tubeRadii[stepIndex] ?? toolStepLayoutConfig.tubeRadius;

    const completeSeeds: CaseSeed[] = [];
    for (const membership of memberships) {
        const { whenStepIndex, whatStepIndex } = membership;

        const whenRingRadius = toRingRadius(whenStepIndex);
        const whatRingRadius = toRingRadius(whatStepIndex);
        const whenTubeRadius = toTubeRadius(whenStepIndex);
        const whatTubeRadius = toTubeRadius(whatStepIndex);

        const geometry = deriveCaseBandGeometry({
            whenRingRadius,
            whatRingRadius,
            whenTubeRadius,
            whatTubeRadius,
            gutter: caseConfig.gutter,
            minWidth: caseConfig.minWidth,
        });
        if (!geometry) {
            continue;
        }

        completeSeeds.push({
            ...membership,
            innerRadius: geometry.innerRadius,
            outerRadius: geometry.outerRadius,
            whenBoundary: geometry.whenBoundary,
        });
    }

    const itemDerivationsByOwnerMacroStepKey = new Map<string, EntityItemDerivation[]>();
    const metadataByItemKey = new Map<EntityItem['itemKey'], CaseMetadata>();

    for (const seed of completeSeeds) {
        const label = seed.slotFamily === 'cases'
            ? `Case ${seed.ownerCaseIndex}`
            : `Case ${seed.ownerCaseIndex + 1}`;

        const itemDerivation = toCaseItemDerivation({
            ownerMacroStepKey: seed.ownerMacroStepKey,
            ownerCaseIndex: seed.ownerCaseIndex,
            slotFamily: seed.slotFamily,
            innerRadius: seed.innerRadius,
            outerRadius: seed.outerRadius,
            whenBoundary: seed.whenBoundary,
            label,
            name: label,
            description: `MacroStep Case band (${seed.slotFamily})`,
            colorNearWhen: caseConfig.colorNearWhen,
            colorNearWhat: caseConfig.colorNearWhat,
            opacity: caseConfig.opacity,
            gradientCutoffCenter: caseConfig.gradientCutoffCenter,
            gradientCutoffWidth: caseConfig.gradientCutoffWidth,
        });

        const current = itemDerivationsByOwnerMacroStepKey.get(seed.ownerMacroStepKey) ?? [];
        current.push(itemDerivation);
        itemDerivationsByOwnerMacroStepKey.set(seed.ownerMacroStepKey, current);

        metadataByItemKey.set(itemDerivation.item.itemKey, {
            ownerMacroStepKey: seed.ownerMacroStepKey,
            ownerCaseIndex: seed.ownerCaseIndex,
            slotFamily: seed.slotFamily,
            whenToolStepKey: seed.whenToolStepKey,
            whatToolStepKey: seed.whatToolStepKey,
            whenStepIndex: seed.whenStepIndex,
            whatStepIndex: seed.whatStepIndex,
        });
    }

    return {
        itemCollectionDerivations: buildCaseItemCollectionDerivations({
            itemDerivationsByOwnerMacroStepKey,
            scope: args.workflowKeyScope,
        }),
        metadataByItemKey,
    };
}